<template>
  <div class="musubi-page musubi-page--lookup">
    <header class="musubi-page__head">
      <h1>Company lookup</h1>
      <p class="sub">Find any UK business by name, number or director — enrich it into your prospects on the spot.</p>
    </header>

    <div class="lookup-bar">
      <span class="lb-ic"><CygnusIcon name="search" /></span>
      <input v-model.trim="query" class="lb-val" type="text" :placeholder="placeholder" @keyup.enter="search" />
      <div class="lb-scope">
        <a v-for="t in types" :key="t.key" href="#" :class="{ on: type === t.key }" @click.prevent="type = t.key">{{ t.label }}</a>
      </div>
      <button class="btn btn-pri btn-sm" :disabled="loading" @click="search">{{ loading ? 'Searching…' : 'Search' }}</button>
    </div>

    <div v-if="gated" class="fbanner warn">
      <CygnusIcon name="lock" /> {{ gatedMsg }}
    </div>
    <div v-else-if="searched" class="lookup-hint">
      Showing matches for <b>"{{ lastQuery }}"</b> · corpus searched first, then the live Companies House register.
    </div>

    <!-- corpus hits -->
    <template v-if="results.corpus && results.corpus.length">
      <div class="res-group-h">
        <span class="rg-t">In your prospects</span>
        <span class="rg-badge corpus">✦ Enriched</span>
        <span class="rg-cnt">{{ results.corpus.length }} already enriched</span>
      </div>
      <div class="lres">
        <div v-for="co in results.corpus" :key="'c' + co.company_id" class="lres-row">
          <div class="lres-av corpus">{{ initials(co.name) }}</div>
          <div class="lres-main">
            <div class="lres-name">{{ co.name }} <span class="ch-status" :class="statusClass(co.status)">{{ prettyStatus(co.status) }}</span></div>
            <div class="lres-sub">
              <span v-if="co.number" class="num">{{ co.number }}</span>
              <span v-if="co.region">{{ co.region }}</span>
              <span v-if="co.staff !== null">{{ co.staff }} staff</span>
            </div>
            <div v-if="co.signals && co.signals.length" class="sigchips">
              <span v-for="(s, i) in co.signals" :key="i" class="sigchip">{{ s }}</span>
            </div>
          </div>
          <div class="lres-right">
            <span v-if="co.in_pipeline" class="in-prospects"><CygnusIcon name="check" /> In a pipeline</span>
            <span v-else-if="co.in_prospects" class="not-yet">In prospects · not enrolled</span>
            <span v-else class="not-yet">Enriched</span>
          </div>
        </div>
      </div>
    </template>

    <!-- fresh CH hits -->
    <template v-if="results.fresh && results.fresh.length">
      <div class="res-group-h">
        <span class="rg-t">On Companies House</span>
        <span class="rg-badge fresh">＋ Not yet in your prospects</span>
        <span class="rg-cnt">{{ results.fresh.length }} fresh from the register</span>
      </div>
      <div class="lres">
        <div v-for="co in results.fresh" :key="'f' + co.number" class="lres-row">
          <div class="lres-av fresh">{{ initials(co.name) }}</div>
          <div class="lres-main">
            <div class="lres-name">{{ co.name }} <span class="ch-status" :class="statusClass(co.status)">{{ prettyStatus(co.status) }}</span></div>
            <div class="lres-sub">
              <span class="num">{{ co.number }}</span>
              <span v-if="co.address_snippet">{{ co.address_snippet }}</span>
              <span v-if="co.via_director">via director {{ co.via_director }}</span>
            </div>
          </div>
          <div class="lres-right">
            <span class="not-yet">{{ co.dissolved ? 'Dissolved · info only' : 'Not enriched yet' }}</span>
            <button class="btn btn-teal btn-sm" :disabled="co.dissolved || enriching === co.number" @click="addEnrich(co)">
              {{ enriching === co.number ? 'Enriching…' : '✦ Add & enrich' }}
            </button>
          </div>
        </div>
      </div>
    </template>

    <div v-if="searched && !loading && !gated && !(results.corpus && results.corpus.length) && !(results.fresh && results.fresh.length)" class="lookup-empty">
      No matches in your corpus or on Companies House for "{{ lastQuery }}".
    </div>

    <!-- enriched detail card -->
    <template v-if="detail">
      <div class="res-group-h">
        <span class="rg-t">After "Add &amp; enrich"</span>
        <span class="rg-badge corpus">✦ Just added</span>
      </div>
      <div class="detail-card">
        <div class="dc-head">
          <div class="dc-av">{{ initials(detail.company.name) }}</div>
          <div>
            <div class="dc-title">{{ detail.company.name }}</div>
            <div class="dc-meta">
              <span class="num">{{ detail.company.number }}</span>
              <span class="ch-status" :class="statusClass(detail.company.status)">{{ prettyStatus(detail.company.status) }}</span>
              <span v-if="detail.company.address">{{ detail.company.address }}</span>
            </div>
          </div>
          <div class="dc-act">
            <span class="enriched-tag">✦ Enrichment queued</span>
          </div>
        </div>
        <div class="dc-foot">
          <span class="df-note">{{ detail.message }} It'll appear in your prospects once enrichment runs.</span>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import useApi from '@apexl/icarus-core/src/services/api';

const DISSOLVED = ['dissolved', 'liquidation', 'converted-closed', 'removed'];

export default {
  name: 'CompanyLookup',
  props: { apiBase: { type: String, default: '/api/v1' } },
  data() {
    return {
      api: useApi(),
      query: '',
      lastQuery: '',
      type: 'name',
      types: [{ key: 'name', label: 'Name' }, { key: 'number', label: 'Number' }, { key: 'director', label: 'Director' }],
      results: { corpus: [], fresh: [] },
      loading: false,
      searched: false,
      gated: false,
      gatedMsg: '',
      enriching: '',
      detail: null,
    };
  },
  computed: {
    placeholder() {
      if (this.type === 'number') return 'e.g. 08841207';
      if (this.type === 'director') return "a director's name";
      return 'e.g. bath wellness';
    },
  },
  methods: {
    unwrap(res) { return res?.data?.data ?? res?.data ?? res; },
    async search() {
      if (this.query.trim().length < 2) return;
      this.loading = true; this.detail = null; this.gated = false;
      this.lastQuery = this.query.trim();
      try {
        const res = await this.api.get(this.apiBase + '/admin/company-lookup', { params: { q: this.query.trim(), type: this.type } }).catch(e => e?.response);
        const d = this.unwrap(res);
        if (d && d.ok) {
          this.results = { corpus: d.corpus || [], fresh: d.fresh || [] };
          this.searched = true;
        } else if (res && res.status === 403) {
          this.gated = true; this.gatedMsg = (d && d.error) || 'Company lookup is an Enterprise feature.';
        } else {
          this.results = { corpus: [], fresh: [] }; this.searched = true;
        }
      } catch (e) {
        this.results = { corpus: [], fresh: [] }; this.searched = true;
      } finally { this.loading = false; }
    },
    async addEnrich(co) {
      if (co.dissolved) return;
      this.enriching = co.number;
      try {
        const res = await this.api.post(this.apiBase + '/admin/company-lookup/add-enrich', { company_number: co.number }).catch(e => e?.response);
        const d = this.unwrap(res);
        if (d && d.ok) {
          this.detail = d;
          // move the hit out of "fresh"
          this.results.fresh = this.results.fresh.filter(f => f.number !== co.number);
        }
      } catch (e) { /* ignore */ }
      finally { this.enriching = ''; }
    },
    initials(name) { return String(name || '?').replace(/[^A-Za-z ]/g, '').split(' ').filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('') || '?'; },
    statusClass(s) { return DISSOLVED.includes(String(s || '').toLowerCase()) ? 'dissolved' : 'active'; },
    prettyStatus(s) { const v = String(s || '').toLowerCase(); if (!v) return 'Unknown'; return v.charAt(0).toUpperCase() + v.slice(1).replace(/-/g, ' '); },
  },
};
</script>

<style scoped>
.musubi-page--lookup { padding: 24px 28px 60px; max-width: 1000px; }
.musubi-page__head h1 { font-size: 1.4rem; letter-spacing: -.01em; }
.musubi-page__head .sub { color: var(--m-text-secondary, #697086); margin-top: 3px; font-size: .85rem; }
.lookup-bar { display: flex; align-items: center; gap: 10px; background: var(--m-bg-card, #fff); border: 1.5px solid var(--m-border, #EAECF3); border-radius: 11px; padding: 10px 14px; box-shadow: var(--m-shadow-card); margin-top: 18px; }
.lb-ic { color: var(--m-text-tertiary, #9AA0AC); }
.lb-val { flex: 1; border: 0; outline: 0; font: inherit; font-size: .92rem; background: transparent; color: var(--m-text-primary, #1A1C23); }
.lb-scope { display: inline-flex; border: 1px solid var(--m-border, #EAECF3); border-radius: 8px; overflow: hidden; }
.lb-scope a { font-size: .74rem; font-weight: 600; color: var(--m-text-tertiary, #9AA0AC); padding: 6px 11px; text-decoration: none; border-right: 1px solid var(--m-border, #EAECF3); }
.lb-scope a:last-child { border-right: 0; }
.lb-scope a.on { background: var(--m-accent-soft, #EEF0FF); color: var(--m-accent, #4F46E5); }
.btn { display: inline-flex; align-items: center; justify-content: center; gap: 6px; font: inherit; font-weight: 650; border-radius: 8px; padding: 8px 14px; cursor: pointer; border: 1px solid transparent; }
.btn-sm { padding: 7px 12px; font-size: .8rem; }
.btn-pri { background: var(--m-accent, #4F46E5); color: #fff; }
.btn-teal { background: var(--m-success, #0F766E); color: #fff; }
.btn:disabled { opacity: .5; cursor: default; }
.lookup-hint { font-size: .74rem; color: var(--m-text-tertiary, #9AA0AC); margin: 8px 2px 0; }
.lookup-empty { padding: 26px; color: var(--m-text-tertiary, #9AA0AC); font-size: .86rem; }
.fbanner.warn { background: #FDF3E4; color: #92400E; border-radius: 9px; padding: 11px 13px; font-size: .84rem; margin: 12px 0; display: flex; gap: 8px; align-items: center; }
.res-group-h { display: flex; align-items: center; gap: 9px; margin: 20px 0 10px; }
.rg-t { font-size: .72rem; font-weight: 800; letter-spacing: .07em; text-transform: uppercase; color: var(--m-text-secondary, #697086); }
.rg-cnt { font-size: .72rem; color: var(--m-text-tertiary, #9AA0AC); }
.rg-badge { font-size: .58rem; font-weight: 800; letter-spacing: .05em; text-transform: uppercase; padding: 2px 8px; border-radius: 999px; }
.rg-badge.corpus { color: #0F766E; background: #E9FBF8; border: 1px solid #B8EDE5; }
.rg-badge.fresh { color: #0284C7; background: #E6F4FC; border: 1px solid #BEE3F5; }
.lres { background: var(--m-bg-card, #fff); border: 1px solid var(--m-border, #EAECF3); border-radius: 12px; box-shadow: var(--m-shadow-card); overflow: hidden; }
.lres-row { display: flex; align-items: center; gap: 14px; padding: 14px 16px; border-bottom: 1px solid var(--m-border, #EAECF3); }
.lres-row:last-child { border-bottom: 0; }
.lres-av { width: 38px; height: 38px; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: .82rem; font-weight: 800; flex: none; }
.lres-av.corpus { background: #E9FBF8; color: #0F766E; }
.lres-av.fresh { background: #E6F4FC; color: #0284C7; }
.lres-main { flex: 1; min-width: 0; }
.lres-name { font-size: .9rem; font-weight: 700; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.lres-sub { font-size: .75rem; color: var(--m-text-tertiary, #9AA0AC); margin-top: 2px; display: flex; flex-wrap: wrap; gap: 4px 12px; }
.lres-sub .num { font-family: var(--m-font-mono, monospace); }
.sigchips { display: flex; gap: 6px; flex-wrap: wrap; margin-top: 7px; }
.sigchip { font-size: .68rem; font-weight: 600; border-radius: 6px; padding: 3px 8px; border: 1px solid var(--m-border, #EAECF3); background: var(--m-bg-content, #F5F6FA); color: var(--m-text-secondary, #697086); }
.lres-right { flex: none; text-align: right; display: flex; flex-direction: column; align-items: flex-end; gap: 8px; }
.in-prospects { font-size: .68rem; font-weight: 700; color: #0F766E; background: #E9FBF8; border: 1px solid #B8EDE5; border-radius: 999px; padding: 3px 9px; }
.not-yet { font-size: .72rem; color: var(--m-text-tertiary, #9AA0AC); }
.ch-status { font-size: .64rem; font-weight: 800; letter-spacing: .03em; text-transform: uppercase; padding: 2px 8px; border-radius: 999px; }
.ch-status.active { color: #0D9488; background: #E9FBF8; }
.ch-status.dissolved { color: #E11D48; background: #FEECF0; }
.detail-card { background: var(--m-bg-card, #fff); border: 1px solid var(--m-border, #EAECF3); border-radius: 14px; box-shadow: var(--m-shadow-panel); overflow: hidden; margin-top: 12px; }
.dc-head { display: flex; align-items: flex-start; gap: 14px; padding: 18px 20px; border-bottom: 1px solid var(--m-border, #EAECF3); background: linear-gradient(180deg, #E9FBF8, #fff); }
.dc-av { width: 46px; height: 46px; border-radius: 12px; background: #fff; border: 1px solid #B8EDE5; color: #0F766E; display: flex; align-items: center; justify-content: center; font-size: 1rem; font-weight: 800; }
.dc-title { font-size: 1.1rem; font-weight: 800; }
.dc-meta { font-size: .76rem; color: var(--m-text-secondary, #697086); margin-top: 3px; display: flex; flex-wrap: wrap; gap: 4px 12px; }
.dc-meta .num { font-family: var(--m-font-mono, monospace); }
.dc-act { margin-left: auto; }
.enriched-tag { font-size: .64rem; font-weight: 800; letter-spacing: .05em; text-transform: uppercase; color: #0F766E; background: #E9FBF8; border: 1px solid #B8EDE5; border-radius: 999px; padding: 3px 9px; }
.dc-foot { padding: 14px 20px; background: var(--m-bg-content, #F5F6FA); }
.df-note { font-size: .74rem; color: var(--m-text-tertiary, #9AA0AC); }
</style>
